import { Box, DialogContent, DialogTitle } from "@mui/material";
import { workOrderPoBusiness } from "api/bussiness/work-order-po/work-order-po-bussiness";
import {
  IUpdateWorkOrderPOPackageProgressDto,
  IUpdateWorkOrderPOProgressDto,
  WorkOrderPoProcessType,
} from "api/models/request/work-order-po/work-order-po-process-update";
import { DynamicForm } from "commons/ultilities/dynamic-form/components/dynamic-form";
import { useForm } from "commons/ultilities/dynamic-form/hooks/use-form";
import { IControl } from "commons/ultilities/dynamic-form/models/control";
import { observer } from "mobx-react-lite";
import {
  IPackageProgressModel,
  IPrintProgressModel,
  IAcceptanceProgressModel,
} from "mobx-stores/_models/work-order-po/progress/progress-models";
import { IWorkOrderPOProgressModel } from "mobx-stores/_models/work-order-po/progress/work-order-po-progress-base-model";
import { useCallback, useEffect } from "react";
import { DialogForm } from "share-components/templates/form/dialog-form";
import {
  computeAcceptanceCompletedAmounts,
  computeCompletedAmountFromPackage,
  computePrintCompletedAmount,
  getAcceptanceProcessFormSchema,
  getPackageProcessFormSchema,
  getPrintProcessFormSchema,
  getStandardProcessFormSchema,
  getProcessWithPrintAmountFormSchema,
  WorkOrderProcessUnit,
} from "./schema";
import { IForm } from "commons/ultilities/dynamic-form/models/form";

type EditProcessDialogProps = {
  open: boolean;
  processType: WorkOrderPoProcessType;
  workOrderPoId: string;
  progress: IWorkOrderPOProgressModel;
  packageProgress: IPackageProgressModel;
  onClose: () => void;
  onSaved: () => Promise<void>;
};

const getProcessTitle = (processType: WorkOrderPoProcessType) => {
  switch (processType) {
    case "paperOutput":
      return "Cập nhật tiến độ xuất giấy";
    case "print":
      return "Cập nhật tiến độ in ấn";
    case "matte":
      return "Cập nhật tiến độ xử lý láng";
    case "acceptance":
      return "Cập nhật tiến độ nghiệm thu";
    case "foil":
      return "Cập nhật tiến độ ép nhũ";
    case "shape":
      return "Cập nhật tiến độ định hình";
    case "paste":
      return "Cập nhật tiến độ dán";
    case "cut":
      return "Cập nhật tiến độ cắt";
    case "package":
      return "Cập nhật tiến độ đóng gói";
    default:
      return "Cập nhật tiến độ";
  }
};

const toBaseProgressDto = (
  progress: IWorkOrderPOProgressModel,
): IUpdateWorkOrderPOProgressDto => {
  // Progress passed in can be any process type (print/acceptance expose the
  // grade fields on their composed models), so cast to access them.
  const gradeProgress = progress as unknown as IPrintProgressModel &
    IAcceptanceProgressModel;

  return {
    date: progress?.date || undefined,
    amount: progress?.amount || undefined,
    equipment: progress?.equipment || undefined,
    operator: progress?.operator || undefined,
    inspector: progress?.inspector || undefined,
    completedDate: progress?.completedDate || undefined,
    completedAmount: progress?.completedAmount || undefined,
    printPaperAmount: progress?.printPaperAmount || undefined,
    printPaperCompletedAmount: progress?.printPaperCompletedAmount || undefined,
    firstGradeAmount: gradeProgress?.firstGradeAmount || undefined,
    secondGradeAmount: gradeProgress?.secondGradeAmount || undefined,
    rejectedAmount: gradeProgress?.rejectedAmount || undefined,
    firstGradeAmountPcs: gradeProgress?.firstGradeAmountPcs || undefined,
    secondGradeAmountPcs: gradeProgress?.secondGradeAmountPcs || undefined,
    rejectedAmountPcs: gradeProgress?.rejectedAmountPcs || undefined,
    firstGradeAmountPaper: gradeProgress?.firstGradeAmountPaper || undefined,
    secondGradeAmountPaper: gradeProgress?.secondGradeAmountPaper || undefined,
    rejectedAmountPaper: gradeProgress?.rejectedAmountPaper || undefined,
  };
};

const toPackageProgressDto = (
  progress: IPackageProgressModel,
): IUpdateWorkOrderPOPackageProgressDto => ({
  ...toBaseProgressDto(progress),
  packageNumber: progress?.packageNumber || undefined,
  packageAmountPerUnit: progress?.packageAmountPerUnit || undefined,
  packageWeightPerUnit: progress?.packageWeightPerUnit || undefined,
  packageAmount: progress?.packageAmount || undefined,
  packageWeight: progress?.packageWeight || undefined,
  packageAmount2: progress?.packageAmount2 || undefined,
  packageWeight2: progress?.packageWeight2 || undefined,
  packageNote: progress?.packageNote || undefined,
});

export const EditProcessDialog = observer(
  ({
    open,
    processType,
    workOrderPoId,
    progress,
    packageProgress,
    onClose,
    onSaved,
  }: EditProcessDialogProps) => {
    const isPackage = processType === "package";
    function getFormSchema(form: IForm, processType: string) {
      if (processType === 'package') {
        return getPackageProcessFormSchema(form);
      }
      if (processType === 'print') {
        return getPrintProcessFormSchema(form);
      }
      if (processType === 'acceptance') {
        return getAcceptanceProcessFormSchema(form);
      }
      if (processType === 'foil' || processType === 'shape') {
        return getProcessWithPrintAmountFormSchema(form);
      }
      let unit: WorkOrderProcessUnit = 'paper';
      if (processType === 'paste' || processType === 'cut' || processType === 'package') {
        unit = 'pcs';
      }
      return getStandardProcessFormSchema(form, unit);
    }
    const {
      form: { controls, isValid },
      getControl,
      setDefaults,
      submitHandler,
    } = useForm<IUpdateWorkOrderPOPackageProgressDto>(
      (form) => getFormSchema(form, processType),
      toPackageProgressDto(packageProgress),
    );

    useEffect(() => {
      setDefaults(
        isPackage
          ? toPackageProgressDto(packageProgress)
          : toBaseProgressDto(progress),
      );
    }, [isPackage, packageProgress, progress, setDefaults]);

    const revalidateControls = useCallback(
      async (
        controlNames: Array<keyof IUpdateWorkOrderPOPackageProgressDto>,
      ) => {
        await Promise.all(
          controlNames.map((controlName) => getControl(controlName).validate()),
        );
      },
      [getControl],
    );

    const onProcessValueChanged = useCallback(
      async (control: IControl) => {
        if (control.name === "amount" || control.name === "completedAmount") {
          await revalidateControls(["amount", "completedAmount"]);
        }

        if (control.name === "printPaperAmount" || control.name === "printPaperCompletedAmount") {
          await revalidateControls(["printPaperAmount", "printPaperCompletedAmount"]);
        }

        // Print: recompute CompletedAmount = FirstGradeAmount + SecondGradeAmount
        if (
          processType === "print" &&
          ["firstGradeAmount", "secondGradeAmount"].includes(control.name)
        ) {
          const firstGradeAmount = getControl("firstGradeAmount")?.value as
            | number
            | null
            | undefined;
          const secondGradeAmount = getControl("secondGradeAmount")?.value as
            | number
            | null
            | undefined;
          const completedAmount = computePrintCompletedAmount(
            firstGradeAmount,
            secondGradeAmount,
          );
          await getControl("completedAmount").setValueAndValidate(
            completedAmount,
          );
          await revalidateControls([
            "firstGradeAmount",
            "secondGradeAmount",
            "rejectedAmount",
            "completedAmount",
          ]);
        }

        // Acceptance: recompute CompletedAmount (pcs) and PrintPaperCompletedAmount (paper)
        if (
          processType === "acceptance" &&
          [
            "firstGradeAmountPcs",
            "secondGradeAmountPcs",
            "firstGradeAmountPaper",
            "secondGradeAmountPaper",
          ].includes(control.name)
        ) {
          const firstGradeAmountPcs = getControl("firstGradeAmountPcs")?.value as
            | number
            | null
            | undefined;
          const secondGradeAmountPcs = getControl("secondGradeAmountPcs")?.value as
            | number
            | null
            | undefined;
          const firstGradeAmountPaper = getControl("firstGradeAmountPaper")?.value as
            | number
            | null
            | undefined;
          const secondGradeAmountPaper = getControl("secondGradeAmountPaper")?.value as
            | number
            | null
            | undefined;
          const { completedAmount, printPaperCompletedAmount } =
            computeAcceptanceCompletedAmounts(
              firstGradeAmountPcs,
              secondGradeAmountPcs,
              firstGradeAmountPaper,
              secondGradeAmountPaper,
            );
          await getControl("completedAmount").setValueAndValidate(
            completedAmount,
          );
          await getControl("printPaperCompletedAmount").setValueAndValidate(
            printPaperCompletedAmount,
          );
          await revalidateControls([
            "firstGradeAmountPcs",
            "secondGradeAmountPcs",
            "rejectedAmountPcs",
            "firstGradeAmountPaper",
            "secondGradeAmountPaper",
            "rejectedAmountPaper",
            "completedAmount",
            "printPaperCompletedAmount",
          ]);
        }

        if (
          isPackage &&
          [
            "packageNumber",
            "packageAmountPerUnit",
            "packageAmount",
            "packageAmount2",
          ].includes(control.name)
        ) {
          const packageNumber = getControl("packageNumber")?.value as
            | number
            | null
            | undefined;
          const packageAmountPerUnit = getControl("packageAmountPerUnit")?.value as
            | number
            | null
            | undefined;
          const packageAmount = getControl("packageAmount")?.value as
            | number
            | null
            | undefined;
          const packageAmount2 = getControl("packageAmount2")?.value as
            | number
            | null
            | undefined;
          const completedAmount = computeCompletedAmountFromPackage(packageNumber, packageAmountPerUnit, packageAmount, packageAmount2);
          await getControl("completedAmount").setValueAndValidate(
            completedAmount,
          );
        }

        if (
          isPackage &&
          [
            "completedAmount",
            "packageNumber",
            "packageAmountPerUnit",
            "packageAmount",
            "packageAmount2",
          ].includes(control.name)
        ) {
          await revalidateControls([
            "completedAmount",
            "packageNumber",
            "packageAmountPerUnit",
            "packageAmount",
            "packageAmount2",
          ]);
        }
      },
      [isPackage, revalidateControls, getControl, processType],
    );

    const saveHandler = useCallback(
      async (data: IUpdateWorkOrderPOPackageProgressDto) => {
        switch (processType) {
          case "paperOutput":
            await workOrderPoBusiness.updatePaperOutputProgress(
              workOrderPoId,
              data,
            );
            break;
          case "print":
            await workOrderPoBusiness.updatePrintProgress(workOrderPoId, data);
            break;
          case "matte":
            await workOrderPoBusiness.updateMatteProgress(workOrderPoId, data);
            break;
          case "acceptance":
            await workOrderPoBusiness.updateAcceptanceProgress(
              workOrderPoId,
              data,
            );
            break;
          case "foil":
            await workOrderPoBusiness.updateFoilProgress(workOrderPoId, data);
            break;
          case "shape":
            await workOrderPoBusiness.updateShapeProgress(workOrderPoId, data);
            break;
          case "paste":
            await workOrderPoBusiness.updatePasteProgress(workOrderPoId, data);
            break;
          case "cut":
            await workOrderPoBusiness.updateCutProgress(workOrderPoId, data);
            break;
          case "package":
            await workOrderPoBusiness.updatePackageProgress(
              workOrderPoId,
              data,
            );
            break;
          default:
            break;
        }

        await onSaved();
        onClose();
      },
      [onClose, onSaved, processType, workOrderPoId],
    );

    return (
      <DialogForm
        open={open}
        fullWidth
        maxWidth={isPackage ? "md" : "sm"}
        onClose={onClose}
        onCancelBtnClicked={onClose}
        onSaveBtnClick={async () => await submitHandler(saveHandler)}
        saveBtnProps={{
          disabled: !isValid,
        }}
      >
        <DialogTitle>{getProcessTitle(processType)}</DialogTitle>
        <DialogContent>
          <Box sx={{ marginTop: "10px" }}>
            <DynamicForm
              controls={controls}
              onValueChanged={onProcessValueChanged}
            ></DynamicForm>
          </Box>
        </DialogContent>
      </DialogForm>
    );
  },
);
